import { Camera } from "../../../SharedGL/Camera";
import { IDisplayBidNFA, INonFlyingAssignment } from "@vmosolutions/oc-interfaces";
import { hexToIRGBA } from "../../../SharedGL/TimelineUtils";
import { PairingShader } from "./shaders/PairingShader";
import { TextureShader } from "./shaders/TextureShader";
import { PairingBuffer } from "./shaders/PairingBuffer";
import { FullDayBuffer } from "./FullDayBuffer";
import { IIndexRange, getNFAsInRange } from "../TimelineUtils";
import { crewBidRowHeight } from "src/components/CrewSchedule/CrewMemberBidding";
import { BidItemStatus, returnBidStatus } from "./PairingsGL";

const pColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  pColors.push(...[0.54, 0.55, 0.52, 0.25]);
});

const nColors: number[] = [];
[0, 0, 0, 0, 0, 0].forEach(() => {
  nColors.push(...[0.0, 1.0, 0.0, 1.0]);
});

//const removedBidNFAColors: number[] = [];
//[0, 0, 0, 0, 0, 0].forEach(() => {
//  removedBidNFAColors.push(...[1, 0, 0, 0.5]); // used to draw awarded bid pairings that are being unwarded.
//});

interface IVertexCountOffset {
  [nfaId: string]: {
    fullDayOffset: number;
    fullDayVertexCount: number;
    dutyOffset: number;
    dutyVertexCount: number;
  };
}

export const addNFAToArr = (
  nfa: INonFlyingAssignment,
  y0: number,
  y: number,
  positions: number[],
  colors: number[]
): void => {
  const x0 = nfa.start?.valueOf() - Camera.referenceTime;
  const x1 = nfa.end?.valueOf() - Camera.referenceTime;

  const vPositions = [x0, y0, x1, y0, x1, y, x0, y0, x1, y, x0, y];

  positions.push(...vPositions);
  const rgba = hexToIRGBA(nfa.assignmentType.defaultColor);
  const alpha = nfa.assignmentType.defaultColorAlpha ?? 0.6;
  const color = [rgba.red, rgba.green, rgba.blue, alpha];
  colors.push(...[...color, ...color, ...color, ...color, ...color, ...color]);
};

/**
 * Bid NFAs can include duplicates assigned to different ranks, to support this, include pref rank as part of the pairing id.
 * @param bidNFAs -
 * @returns
 */
const includeRankInBidNFAID = (bidNFAs: IDisplayBidNFA[]): IDisplayBidNFA[] => {
  return bidNFAs?.map((bnfa) => {
    const bidNFAIdRank = `${bnfa._id}_rank${bnfa.prefRank}`;
    return { ...bnfa, _id: bidNFAIdRank };
  });
};

export class NonFlyingAssignmentsGL {
  // Non Flying Assignments
  public static yStart = 12;
  public static height = 26;
  private nonFlyingAssignmentData: INonFlyingAssignment[] = [];
  private nfaVertexCountOffset: IVertexCountOffset = {};
  private fullDayBuffer: FullDayBuffer; // TextureShader;
  private fullDayVertexCount = 0;
  private dutyBuffer: PairingBuffer;
  private dutyVertexCount = 0;
  public lastModified = 0;

  // Bid Non flying Assignments
  private bidNonFlyingAssignmentData: IDisplayBidNFA[] = [];
  private bidNfaVertexCountOffset: IVertexCountOffset = {};
  private bidFullDayBuffer: FullDayBuffer; // TextureShader;
  private bidFullDayVertexCount = 0;
  private bidDutyBuffer: PairingBuffer;
  private bidDutyVertexCount = 0;
  public bidNfaLastModified = 0;

  constructor(
    gl: WebGLRenderingContext,
    fullDayShader: TextureShader,
    dutyShader: PairingShader,
    texture: WebGLTexture
  ) {
    this.fullDayBuffer = new FullDayBuffer(gl, fullDayShader, texture); // new GroundEventShader(gl, texture);
    this.dutyBuffer = new PairingBuffer(gl, dutyShader); // new PairingShader(gl);
    this.bidFullDayBuffer = new FullDayBuffer(gl, fullDayShader, texture);
    this.bidDutyBuffer = new PairingBuffer(gl, dutyShader);
  }

  public updateBidNfaBuffers(gl: WebGLRenderingContext, bidNFAs: IDisplayBidNFA[]): void {
    const bidNFAsRank = includeRankInBidNFAID(bidNFAs);
    this.bidNonFlyingAssignmentData = bidNFAsRank;

    //const nfaY0 = NonFlyingAssignmentsGL.yStart;
    //const nfaY1 = nfaY0 + NonFlyingAssignmentsGL.height;

    const bidFullDayNFAPositions: number[] = [];
    const bidFullDayNFAColors: number[] = [];
    const bidDutyNFAPositions: number[] = [];
    const bidDutyNFAColors: number[] = [];

    bidNFAs.forEach((bidNFA: IDisplayBidNFA) => {
      const stagingRowHeight = crewBidRowHeight + 4;
      const nfaY0 =
        NonFlyingAssignmentsGL.yStart +
        28 +
        stagingRowHeight +
        bidNFA.prefRank * crewBidRowHeight +
        bidNFA.prefRank * 4;
      const nfaY1 = nfaY0 + crewBidRowHeight;

      if (this.bidNfaVertexCountOffset[bidNFA._id]) {
        this.bidNfaVertexCountOffset[bidNFA._id].fullDayOffset = bidFullDayNFAPositions.length / 2;
        this.bidNfaVertexCountOffset[bidNFA._id].dutyOffset = bidDutyNFAPositions.length / 2;
      } else {
        this.bidNfaVertexCountOffset[bidNFA._id] = {
          fullDayOffset: bidFullDayNFAPositions.length / 2,
          fullDayVertexCount: 0,
          dutyOffset: bidDutyNFAPositions.length / 2,
          dutyVertexCount: 0,
        };
      }
      const x0 = bidNFA.start?.valueOf() - Camera.referenceTime;
      const x1 = bidNFA.end?.valueOf() - Camera.referenceTime;
      const positions = [x0, nfaY0, x1, nfaY0, x1, nfaY1, x0, nfaY0, x1, nfaY1, x0, nfaY1];
      // const [r, g, b] = hexToRgb(nfa.assignmentType.defaultColor);
      const rgba = hexToIRGBA(bidNFA.assignmentType.defaultColor);
      const isAssignedOrAwarded = (bidNFA?.assigned && !bidNFA?.awarded) || (!bidNFA?.assigned && bidNFA?.awarded);

      // If bid NFA is assigned for awarding or unawarding, render it in a ghosted form to better highlight it's staged version.
      const assignedToAward = returnBidStatus(bidNFA) === BidItemStatus.assigned_in_rank_row;
      const assignedToUnaward = returnBidStatus(bidNFA) === BidItemStatus.assigned_to_unaward_in_rank_row;
      const assignedBid = assignedToAward || assignedToUnaward;

      const baseAlpha = bidNFA.assignmentType.defaultColorAlpha ?? 0.3;
      const alpha = assignedBid ? Math.min(baseAlpha * 0.33, 0.1) : baseAlpha; // assignedOrAwarded bid nfas stay ghosted
      const color = [rgba.red, rgba.green, rgba.blue, alpha];
      const colors = [...color, ...color, ...color, ...color, ...color, ...color];

      if (!bidNFA.assignmentType.isDuty) {
        bidFullDayNFAPositions.push(...positions);
        bidFullDayNFAColors.push(...colors);
      } else {
        bidDutyNFAPositions.push(...positions);
        bidDutyNFAColors.push(...colors);
      }

      // Bid NFA is assigned, create a copy of it in the buffer and render this one in staging row.
      if (isAssignedOrAwarded) {
        const assignedBidNFA = { ...bidNFA, _id: `${bidNFA._id}_staged` };
        const nfaY0 = NonFlyingAssignmentsGL.yStart + 28 + stagingRowHeight;
        const nfaY1 = nfaY0 + crewBidRowHeight;

        if (this.bidNfaVertexCountOffset[assignedBidNFA._id]) {
          this.bidNfaVertexCountOffset[assignedBidNFA._id].fullDayOffset = bidFullDayNFAPositions.length / 2;
          this.bidNfaVertexCountOffset[assignedBidNFA._id].dutyOffset = bidDutyNFAPositions.length / 2;
        } else {
          this.bidNfaVertexCountOffset[assignedBidNFA._id] = {
            fullDayOffset: bidFullDayNFAPositions.length / 2,
            fullDayVertexCount: 0,
            dutyOffset: bidDutyNFAPositions.length / 2,
            dutyVertexCount: 0,
          };
        }
        const x0 = assignedBidNFA.start?.valueOf() - Camera.referenceTime;
        const x1 = assignedBidNFA.end?.valueOf() - Camera.referenceTime;
        const positions = [x0, nfaY0, x1, nfaY0, x1, nfaY1, x0, nfaY0, x1, nfaY1, x0, nfaY1];
        // const [r, g, b] = hexToRgb(nfa.assignmentType.defaultColor);
        const rgba = hexToIRGBA(assignedBidNFA.assignmentType.defaultColor);
        const baseAlpha = assignedBidNFA.assignmentType.defaultColorAlpha ?? 0.3;
        const color = [rgba.red, rgba.green, rgba.blue, baseAlpha];
        const colors = [...color, ...color, ...color, ...color, ...color, ...color];
        if (!assignedBidNFA.assignmentType.isDuty) {
          bidFullDayNFAPositions.push(...positions);
          bidFullDayNFAColors.push(...colors);
        } else {
          bidDutyNFAPositions.push(...positions);
          bidDutyNFAColors.push(...colors);
        }
      }

      this.bidNfaVertexCountOffset[bidNFA._id].fullDayVertexCount = bidFullDayNFAPositions.length / 2;
      this.bidNfaVertexCountOffset[bidNFA._id].dutyVertexCount = bidDutyNFAPositions.length / 2;
    });

    this.bidFullDayBuffer.updateBuffers(gl, bidFullDayNFAPositions, bidFullDayNFAColors);
    this.bidFullDayVertexCount = bidFullDayNFAPositions.length / 2;

    this.bidDutyBuffer.updateBuffers(gl, bidDutyNFAPositions, bidDutyNFAColors);
    this.bidDutyVertexCount = bidDutyNFAPositions.length / 2;

    this.bidNfaLastModified = Date.now();
  }

  public updateBuffers(gl: WebGLRenderingContext, nfas: INonFlyingAssignment[]): void {
    this.nonFlyingAssignmentData = nfas;

    const nfaY0 = NonFlyingAssignmentsGL.yStart;
    const nfaY1 = nfaY0 + NonFlyingAssignmentsGL.height;

    const fullDayNFAPositions: number[] = [];
    const fullDayNFAColors: number[] = [];

    const dutyNFAPositions: number[] = [];
    const dutyNFAColors: number[] = [];

    nfas.forEach((nfa: INonFlyingAssignment) => {
      if (this.nfaVertexCountOffset[nfa._id]) {
        this.nfaVertexCountOffset[nfa._id].fullDayOffset = fullDayNFAPositions.length / 2;
        this.nfaVertexCountOffset[nfa._id].dutyOffset = dutyNFAPositions.length / 2;
      } else {
        this.nfaVertexCountOffset[nfa._id] = {
          fullDayOffset: fullDayNFAPositions.length / 2,
          fullDayVertexCount: 0,
          dutyOffset: dutyNFAPositions.length / 2,
          dutyVertexCount: 0,
        };
      }
      const x0 = nfa.start?.valueOf() - Camera.referenceTime;
      const x1 = nfa.end?.valueOf() - Camera.referenceTime;
      const positions = [x0, nfaY0, x1, nfaY0, x1, nfaY1, x0, nfaY0, x1, nfaY1, x0, nfaY1];
      // const [r, g, b] = hexToRgb(nfa.assignmentType.defaultColor);
      const rgba = hexToIRGBA(nfa.assignmentType.defaultColor);
      const alpha = nfa.assignmentType.defaultColorAlpha ?? 0.3;
      const color = [rgba.red, rgba.green, rgba.blue, alpha];
      const colors = [...color, ...color, ...color, ...color, ...color, ...color];
      if (!nfa.assignmentType.isDuty) {
        fullDayNFAPositions.push(...positions);
        fullDayNFAColors.push(...colors);
      } else {
        dutyNFAPositions.push(...positions);
        dutyNFAColors.push(...colors);
      }

      this.nfaVertexCountOffset[nfa._id].fullDayVertexCount = fullDayNFAPositions.length / 2;
      this.nfaVertexCountOffset[nfa._id].dutyVertexCount = dutyNFAPositions.length / 2;
    });

    this.fullDayBuffer.updateBuffers(gl, fullDayNFAPositions, fullDayNFAColors);
    this.fullDayVertexCount = fullDayNFAPositions.length / 2;

    this.dutyBuffer.updateBuffers(gl, dutyNFAPositions, dutyNFAColors);
    this.dutyVertexCount = dutyNFAPositions.length / 2;

    this.lastModified = Date.now();
  }

  public draw = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    const [minX, maxX] = camera.getXRange();
    const range: IIndexRange = getNFAsInRange(camera, this.nonFlyingAssignmentData);

    if (range.minIndex < 0 || range.maxIndex < 0 || range.minIndex > range.maxIndex) {
      return;
    }
    //get ids of the min and max nfas
    const minNFAId = this.nonFlyingAssignmentData[range.minIndex]._id;
    const maxNFAId = this.nonFlyingAssignmentData[range.maxIndex]._id;
    const fullDayOffset = this.nfaVertexCountOffset[minNFAId] ? this.nfaVertexCountOffset[minNFAId].fullDayOffset : 0;
    const fullDayVC = this.nfaVertexCountOffset[maxNFAId]
      ? this.nfaVertexCountOffset[maxNFAId].fullDayVertexCount - fullDayOffset
      : this.fullDayVertexCount;

    const xScale = gl.canvas.width / (maxX - Camera.referenceTime - (minX - Camera.referenceTime));
    const scale = [xScale, 0.01666];

    // const offset = 0; //minIndex * 6;
    // const numVerts = 6; //(maxIndex + 1) * 6 - offset;
    if (fullDayVC > 0) {
      this.fullDayBuffer.draw(gl, camera, yStart, fullDayOffset, fullDayVC, scale);
    }

    const dutyOffset = this.nfaVertexCountOffset[minNFAId] ? this.nfaVertexCountOffset[minNFAId].dutyOffset : 0;
    const dutyVC = this.nfaVertexCountOffset[maxNFAId]
      ? this.nfaVertexCountOffset[maxNFAId].dutyVertexCount - dutyOffset
      : this.dutyVertexCount;

    if (dutyVC > 0) {
      this.dutyBuffer.draw(gl, camera, yStart, -4, dutyOffset, dutyVC);
    }
  };

  public drawBidNFAs = (gl: WebGLRenderingContext, camera: Camera, yStart: number): void => {
    const [minX, maxX] = camera.getXRange();
    const range: IIndexRange = getNFAsInRange(camera, this.bidNonFlyingAssignmentData);

    if (range.minIndex < 0 || range.maxIndex < 0 || range.minIndex > range.maxIndex) {
      return;
    }
    //get ids of the min and max bid nfas
    const minNFAId = this.bidNonFlyingAssignmentData[range.minIndex]._id;
    const maxNFAId = this.bidNonFlyingAssignmentData[range.maxIndex]._id;
    const fullDayOffset = this.bidNfaVertexCountOffset[minNFAId]
      ? this.bidNfaVertexCountOffset[minNFAId].fullDayOffset
      : 0;
    const fullDayVC = this.bidNfaVertexCountOffset[maxNFAId]
      ? this.bidNfaVertexCountOffset[maxNFAId].fullDayVertexCount - fullDayOffset
      : this.bidFullDayVertexCount;

    const xScale = gl.canvas.width / (maxX - Camera.referenceTime - (minX - Camera.referenceTime));
    const scale = [xScale, 0.01666];

    // const offset = 0; //minIndex * 6;
    // const numVerts = 6; //(maxIndex + 1) * 6 - offset;
    if (fullDayVC > 0) {
      this.bidFullDayBuffer.draw(gl, camera, yStart, fullDayOffset, fullDayVC, scale);
    }

    const dutyOffset = this.bidNfaVertexCountOffset[minNFAId] ? this.bidNfaVertexCountOffset[minNFAId].dutyOffset : 0;
    const dutyVC = this.bidNfaVertexCountOffset[maxNFAId]
      ? this.bidNfaVertexCountOffset[maxNFAId].dutyVertexCount - dutyOffset
      : this.bidDutyVertexCount;

    if (dutyVC > 0) {
      this.bidDutyBuffer.draw(gl, camera, yStart, -4, dutyOffset, dutyVC);
    }
  };
}
